import { isPlatformBrowser } from '@angular/common';
/**
 * This class shims the datalayer logic to bridge between the legacy script and Angular or ES6 modules
 * It still depends currently on the legacy datalayer scripts to perform the logic which are
 * only available on the PlatformBrowser.
 *
 * The major dependency are the imported javascripts from the application itself that has the
 * delta.dataLayer = {} functionalities.
 *
 * To mitigate that, the calls to the existing legacy delta.dataLayer.setData() for example,
 * needs to be wrapped with a try catch.
 */
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { SHA256, enc } from 'crypto-js';

declare let idp: any;
declare let delta: any;

@Injectable({
  providedIn: 'root'
})
export class DataLayer {
  private _upcomingTripDta = {
    flightNumber: null,
    originAirportCode: null,
    tripCabin: null,
    tripFareClass: null,
    codeShare: null,
    departureDate: null,
    destinationAirportCode: null,
    irop: null,
    iropSituation: null
  };

  /*Added for datalayer changes ends */
  private _dataMapping = {
    events: 'events',
    pageName: 'pageName',
    linkTrackVars: 'linkTrackVars',
    initialCabin: 'initialCabin',
    eVar19: 'eVar19',
    eVar20: 'eVar20',
    eVar74: 'eVar74',
    eVar83: 'eVar83',
    prop1: 'prop1',
    prop2: 'prop2',
    prop3: 'prop3',
    prop9: 'prop9',
    prop23: 'prop23',
    prop26: 'prop26',
    prop36: 'prop36',
    prop64: 'prop64'
  };

  private _trackingprefix = {
    homepage: 'hp:'
  };

  private _trackingInfo = {};
  // TODO - inject the environment value from the constructor
  private environment = {
    name: 'dvl'
  };

  // TODO - inject the application info from the constructor
  private application = {
    name: 'homepage'
  };

  private _initDataSuccess = true;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private logger: LoggingService
  ) {}

  /*Added for datalayer changes starts */
  private _dataSummary = {
    pageID: '',
    pageName: '',
    pageInstanceID: '',
    primaryCategory: '',
    subCategory: '',
    loggedIn: false,
    sysEnv: null,
    cacheKey: '',
    isIpad: '',
    primaryPaymentType: '',
    ticketType: '',
    searchType: '',
    searchOptions: '',
    searchMethod: '',
    offeredBookingTypes: '',
    customerID: '',
    medallionStatus: '',
    primaryEmailAddress: '',
    smBalance: '',
    smNumber: '',
    loggedInStatus: false,
    crprtAgrmntFound: null,
    enrollmentDate: null,
    is360Tier: null,
    millionMiler: null,
    prefix: null,
    rememberMeIndicator: null,
    skyPriority: null,
    spgInd: null,
    sysDrvdPrefHomeArptCd: null,
    usrProvdPrefHomeArptCd: null,
    cardSpendAmount: null,
    milesExpire: null,
    mqdBalance: null,
    mqmBalance: null,
    mqsBalance: null,
    skyMilesNumberHashed: null,
    tierExpire: null,
    isBusinessAccount: false,
    selectedCompany: null,
    isOaSmeAccount: false,
    warmStateIndicator: false
  };

  /*Added for datalayer changes ends */
  get dataSummary() {
    return this._dataSummary;
  }

  /*Added for datalayer changes starts */
  set customerInfo(customerInfo) {
    if (customerInfo) {
      if (customerInfo['customerID'] != null) {
        this._dataSummary.customerID = customerInfo['customerID'];
      }
      if (customerInfo['membershipLevel'] != null) {
        this._dataSummary.medallionStatus = customerInfo['membershipLevel'];
      }
      if (customerInfo['primaryEmailAddress'] != null) {
        this._dataSummary.primaryEmailAddress = SHA256(
          customerInfo['primaryEmailAddress']
        ).toString(enc.Base64);
      }
      if (customerInfo['smBalance'] != null) {
        this._dataSummary.smBalance = customerInfo['smBalance'];
      }
      if (customerInfo['smNumber'] != null) {
        this._dataSummary.smNumber = customerInfo['smNumber'];
      }
      if (customerInfo['enrollmentDate'] != null) {
        this._dataSummary.enrollmentDate = customerInfo['enrollmentDate'];
      }
      if (customerInfo['is360Tier'] != null) {
        this._dataSummary.is360Tier = customerInfo['is360Tier'];
      }
      if (customerInfo['millionMiler'] != null) {
        this._dataSummary.millionMiler = customerInfo['millionMiler'];
      }
      if (customerInfo['prefix'] != null) {
        this._dataSummary.prefix = customerInfo['prefix'];
      }
      if (customerInfo['rememberMeIndicator'] != null) {
        this._dataSummary.rememberMeIndicator =
          customerInfo['rememberMeIndicator'];
      }
      if (customerInfo['skyPriority'] != null) {
        this._dataSummary.skyPriority = customerInfo['skyPriority'];
      }
      if (customerInfo['spgInd'] != null) {
        this._dataSummary.spgInd = customerInfo['spgInd'];
      }
      if (customerInfo['sysDrvdPrefHomeArptCd'] != null) {
        this._dataSummary.sysDrvdPrefHomeArptCd =
          customerInfo['sysDrvdPrefHomeArptCd'];
      }
      if (customerInfo['usrProvdPrefHomeArptCd'] != null) {
        this._dataSummary.usrProvdPrefHomeArptCd =
          customerInfo['usrProvdPrefHomeArptCd'];
      }
      this._dataSummary.loggedInStatus =
        customerInfo['loggedIn'] !== undefined
          ? customerInfo['loggedIn']
          : false;
      this._dataSummary.loggedIn =
        customerInfo['loggedIn'] !== undefined
          ? customerInfo['loggedIn']
          : false;
      this._dataSummary.crprtAgrmntFound = customerInfo['crprtAgrmntFound'];
      this._dataSummary.warmStateIndicator = customerInfo['isWarmState'];
    }
  }

  set pageInfo(pageInfo) {
    this._dataSummary.pageID = `${this.application.name}`;
    this._dataSummary.pageName = `${this.application.name}`;
    this._dataSummary.pageInstanceID = `${this.application.name}-${this.environment.name}`;
    this._dataSummary.subCategory = `${this.application.name}`;
    this._dataSummary.primaryCategory = `${this.application.name}`;
    this._dataSummary.sysEnv = pageInfo;
  }

  set upcomingTripData(upcomingTripInfo) {
    if (upcomingTripInfo) {
      if (upcomingTripInfo['dateRange'] != null) {
        this._upcomingTripDta.departureDate = upcomingTripInfo['dateRange'];
      }
      if (upcomingTripInfo['destinationCity'] != null) {
        this._upcomingTripDta.destinationAirportCode =
          upcomingTripInfo['destinationCity'];
      }
      if (upcomingTripInfo['iropImpact'] != null) {
        this._upcomingTripDta.irop = upcomingTripInfo['iropImpact'];
      } else {
        this._upcomingTripDta.irop = false;
      }
      if (upcomingTripInfo['iropMessage'] != null) {
        this._upcomingTripDta.iropSituation = upcomingTripInfo['iropMessage'];
      } else {
        this._upcomingTripDta.iropSituation = '';
      }
    }
  }

  set accountActivityData(accountActvtyData) {
    if (accountActvtyData) {
      this._dataSummary.cardSpendAmount =
        accountActvtyData['currentYearCardSpend'];
      // this._dataSummary.milesExpire = accountActvtyData['lifetimeMQM'];
      if (accountActvtyData['currentYearMQD'] != null) {
        this._dataSummary.mqdBalance =
          accountActvtyData['currentYearMQD']['availableBalance'];
      }
      if (accountActvtyData['currentYearMQM'] != null) {
        this._dataSummary.mqmBalance =
          accountActvtyData['currentYearMQM']['availableBalance'];
      }
      if (accountActvtyData['currentYearMQS'] != null) {
        this._dataSummary.mqsBalance =
          accountActvtyData['currentYearMQS']['availableBalance'];
      }
      // this._dataSummary.primaryPaymentType = accountActvtyData['futureProgramYear']; //need to map correctly
      // this._dataSummary.skyMilesNumberHashed = accountActvtyData['futureProgramYear']; //need to map correctly
      // this._dataSummary.tierExpire = accountActvtyData['futureProgramYear']; //need to map correctly
    }
  }

  get dataInitialized() {
    return this._initDataSuccess;
  }

  /**
   * Update the page tracking object depending on the clicked element on the page
   * @param trackingInfoUpdate
   */
  updateTrackingInfo(trackingInfoUpdate) {
    try {
      if (this.isOnBrowser()) {
        const trackType = 'clickedObj';
        if (
          Object.keys(trackingInfoUpdate).length === 0 &&
          trackingInfoUpdate.constructor === Object
        ) {
          trackingInfoUpdate = {};
        }
        this._trackingInfo = Object.assign(
          {},
          this._trackingInfo,
          trackingInfoUpdate
        );
        if (typeof idp !== 'undefined') {
          if (typeof idp[this.application.name] === 'undefined') {
            idp[`${this.application.name}`] = {};
          }
          idp[`${this.application.name}`][`${trackType}`] = this._trackingInfo;
        }
      }
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setTrackingObjectProp1(propValue, target) {
    try {
      const clickedObj = {};
      let customevent;
      if (this.isOnBrowser()) {
        clickedObj[this._dataMapping.prop1] =
          this._trackingprefix[`${this.application.name}`] + propValue;
        customevent =
          this._trackingprefix[`${this.application.name}`] +
          this._dataMapping.prop1 +
          ':' +
          propValue +
          ':click';
        this.updateTrackingInfo(clickedObj);
        target.dispatchEvent(new CustomEvent(customevent, { bubbles: true }));
      }
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  compile() {
    if (this.isOnBrowser()) {
      // The try-catch block is specifically added so that if the legacy dataLayer script is not existing,
      // it should not throw an error.
      try {
        idp.dataLayer.setData({
          core: this._dataSummary,
          flightinfo: this._upcomingTripDta
        });
      } catch (error) {
        this._initDataSuccess = false;
        this.logger.log(`Caught error in datalayer: ${error}`);
      }
    }
  }

  /**
   * This method is called when updating the page tracking object
   * @param componentRef - the Component Reference that will fire the event
   */
  public dataReady(componentRef) {
    try {
      if (this.isOnBrowser()) {
        if (true && this._initDataSuccess) {
          componentRef.nativeElement.dispatchEvent(
            new CustomEvent(`${this.application.name}:data:ready`, {
              bubbles: true
            })
          );
        } else {
          // the initialization call to delta.dataLayer.setData might have failed
        }
      }
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  /**
   * This is called during OnAfterViewInit() of a Component
   */
  init() {
    if (this.isOnBrowser()) {
      // The try-catch block is specifically added so that if the legacy dataLayer script is not existing,
      // it should not throw an error.
      try {
        idp.dataLayer.init();
      } catch (error) {
        this._initDataSuccess = false;
        this.logger.log(`Caught error in datalayer: ${error}`);
      }
    }
  }

  isOnBrowser() {
    try {
      return isPlatformBrowser(this.platformId);
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setDataBrokerInfo(data) {
    // The try-catch block is specifically added so that if the legacy dataLayer script is not existing,
    // it should not throw an error.
    try {
      idp.dataLayer.setDataBrokerInfo(data);
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setClosestDeltaAirportCode(data: string) {
    try {
      if (data) {
        idp.dataLayer.digitalData.attributes.closestDeltaAirportCode = data;
      }
      idp.dataLayer.setData(
        idp.dataLayer.digitalData.attributes.closestDeltaAirportCode
      );
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setSiteSearch(data: string, searchLocation: boolean) {
    try {
      if (searchLocation) {
        idp.dataLayer.digitalData.attributes.siteSearch = {
          header: data,
          page: '',
          footer: ''
        };
      } else {
        idp.dataLayer.digitalData.attributes.siteSearch = {
          header: '',
          page: '',
          footer: data
        };
      }
      idp.dataLayer.setData(idp.dataLayer.digitalData.attributes.siteSearch);
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setDdcfbUserData(response) {
    try {
      if (response) {
        idp.dataLayer.digitalData.user.ddcfbData = {
          crprtAgrmntTypeCode: response['crprtAgrmntTypeCode'],
          pgmType: response['pgmType'],
          pgm: response['program'],
          permissions: response['permType']
        };
      } else {
        idp.dataLayer.digitalData.user.ddcfbData = {
          crprtAgrmntTypeCode: '',
          pgmType: '',
          pgm: '',
          permissions: ''
        };
      }
      idp.dataLayer.setData(idp.dataLayer.digitalData.user.ddcfbData);
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setDdcfbLoginPickerUserData(response) {
    try {
      if (response) {
        delta.dataLayer.digitalData.user.ddcfbData = {
          crprtAgrmntTypeCode: response['crprtAgrmntTypeCode'],
          pgmType: response['pgmType'],
          pgm: response['program'],
          permissions: response['permType']
        };
      } else {
        delta.dataLayer.digitalData.user.ddcfbData = {
          crprtAgrmntTypeCode: '',
          pgmType: '',
          pgm: '',
          permissions: ''
        };
      }
      delta.dataLayer.setData(delta.dataLayer.digitalData.user.ddcfbData);
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setDdcfbData(response, deltaDataLayer?: boolean) {
    try {
      if (deltaDataLayer) {
        this.setDdcfbLoginPickerUserData(response);
      } else {
        this.setDdcfbUserData(response);
      }
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  checkForSettingDdcfbData(data) {
    try {
      return (
        data &&
        data['crprtAgrmntTypeCode'] &&
        data['pgmType'] &&
        data['program'] &&
        !this.checkForddcfbDataAvailable()
      );
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  checkForddcfbDataAvailable() {
    try {
      let dataAlreadyAvailable = false;
      if (typeof idp !== 'undefined') {
        if (
          idp.dataLayer?.digitalData?.user?.ddcfbData &&
          idp.dataLayer.digitalData.user.ddcfbData?.crprtAgrmntTypeCode &&
          idp.dataLayer.digitalData.user.ddcfbData?.pgmType &&
          idp.dataLayer.digitalData.user.ddcfbData?.pgm
        ) {
          dataAlreadyAvailable = true;
        }
      }
      return dataAlreadyAvailable;
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  setCustomerInfo(data, config) {
    try {
      this.customerInfo = data;
      if (config && config.get('APP_ENV')) {
        this.pageInfo = config.get('APP_ENV');
      }
      this.compile();
      this.init();
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  getNestedValue(obj: any, path: string) {
    try {
      return path.split('.').reduce((obj, key) => obj?.[key], obj);
    } catch (error) {
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }

  public setWarmStateFlag(response) {
    try {
      if (typeof idp !== 'undefined') {
        const idpDataLayer = this.getNestedValue(idp, 'dataLayer') as any;
        const idpLoginData = this.getNestedValue(
          idpDataLayer.digitalData.user,
          'loginData'
        );
        const idpProfileInfo = this.getNestedValue(
          idpDataLayer.digitalData.user.profile,
          'profileInfo'
        );
        if (idpDataLayer && idpLoginData && idpProfileInfo) {
          if (response) {
            idp.dataLayer.digitalData.user.profile.attributes.isLoggedIn =
              response['loggedIn'];
            idp.dataLayer.digitalData.user.profile.attributes.skymilesNumber =
              response['smNumber'];
            idp.dataLayer.digitalData.user.loginData.warmState =
              response['isWarmState'];
            idp.dataLayer.digitalData.user.profile.attributes.loyaltyTier =
              response['membershipLevel'];
            idp.dataLayer.digitalData.user.profile.profileInfo.email = SHA256(
              response['primaryEmailAddress']
            ).toString(enc.Base64);
          } else {
            idp.dataLayer.digitalData.user.profile.attributes.isLoggedIn = '';
            idp.dataLayer.digitalData.user.profile.attributes.skymilesNumber =
              '';
            idp.dataLayer.digitalData.user.loginData.warmState = '';
            idp.dataLayer.digitalData.user.profile.attributes.loyaltyTier = '';
            idp.dataLayer.digitalData.user.profile.profileInfo.email = '';
          }
          idp.dataLayer.setData(
            idp.dataLayer.digitalData.user.profile.attributes.isLoggedIn
          );
          idp.dataLayer.setData(
            idp.dataLayer.digitalData.user.profile.attributes.skymilesNumber
          );
          idp.dataLayer.setData(
            idp.dataLayer.digitalData.user.loginData.warmState
          );
          idp.dataLayer.setData(
            idp.dataLayer.digitalData.user.profile.attributes.loyaltyTier
          );
          idp.dataLayer.setData(
            idp.dataLayer.digitalData.user.profile.profileInfo.email
          );
        }
      } else if (typeof idp === 'undefined' && typeof delta !== 'undefined') {
        const deltaDataLayer = this.getNestedValue(delta, 'dataLayer') as any;
        const deltaLoginData = this.getNestedValue(
          deltaDataLayer.digitalData.user,
          'loginData'
        );
        const idpProfileInfo = this.getNestedValue(
          deltaDataLayer.digitalData.user.profile,
          'profileInfo'
        );
        if (deltaDataLayer && deltaLoginData && idpProfileInfo) {
          if (response) {
            delta.dataLayer.digitalData.user.profile.attributes.isLoggedIn =
              response['loggedIn'];
            delta.dataLayer.digitalData.user.profile.attributes.skymilesNumber =
              response['smNumber'];
            delta.dataLayer.digitalData.user.loginData.warmState =
              response['isWarmState'];
            delta.dataLayer.digitalData.user.profile.attributes.loyaltyTier =
              response['membershipLevel'];
            delta.dataLayer.digitalData.user.profile.profileInfo.email = SHA256(
              response['primaryEmailAddress']
            ).toString(enc.Base64);
          } else {
            delta.dataLayer.digitalData.user.profile.attributes.isLoggedIn = '';
            delta.dataLayer.digitalData.user.profile.attributes.skymilesNumber =
              '';
            delta.dataLayer.digitalData.user.loginData.warmState = '';
            delta.dataLayer.digitalData.user.profile.attributes.loyaltyTier =
              '';
            delta.dataLayer.digitalData.user.profile.profileInfo.email = '';
          }
          delta.dataLayer.setData(
            delta.dataLayer.digitalData.user.profile.attributes.isLoggedIn
          );
          delta.dataLayer.setData(
            delta.dataLayer.digitalData.user.profile.attributes.skymilesNumber
          );
          delta.dataLayer.setData(
            delta.dataLayer.digitalData.user.loginData.warmState
          );
          delta.dataLayer.setData(
            delta.dataLayer.digitalData.user.profile.attributes.loyaltyTier
          );
          delta.dataLayer.setData(
            delta.dataLayer.digitalData.user.profile.profileInfo.email
          );
        }
      }
    } catch (error) {
      this._initDataSuccess = false;
      this.logger.log(`Caught error in datalayer: ${error}`);
    }
  }
}
